import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Heart, Users, BookOpen, Scale, ArrowRight, Info } from "lucide-react";
import { motion } from "framer-motion";

import missionImage from "@/assets/mission-counseling.webp";
import { usePageMeta } from "@/hooks/usePageMeta";

const focusAreas = [
{
  icon: Heart,
  title: "Counseling Services",
  desc: "Free and reduced-fee counseling for individuals and families facing financial hardship."
},
{
  icon: Users,
  title: "Community Outreach",
  desc: "Connecting underserved communities with mental health resources and support networks."
},
{
  icon: BookOpen,
  title: "Mental Health Education",
  desc: "Workshops and programs that promote emotional wellness and mental health awareness."
},
{
  icon: Scale,
  title: "Mental Health Equity",
  desc: "Advocating for equal access to mental health care regardless of income or insurance status."
}];


const fade = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5 }
  })
};

const Index = () => {
  usePageMeta(
    "Open Hands Healing Foundation | NE Ohio Mental Health",
    "Free and reduced-fee counseling for individuals and families in Northeast Ohio. Mental health equity from Twinsburg, Ohio.",
    "/"
  );
  return (
    <main>
      {/* Hero */}
      <section className="relative overflow-hidden py-24 md:py-36">
        <img
          src="/hero-community.webp"
          alt="Community members joining hands in support"
          className="absolute inset-0 h-full w-full object-cover"
          width="1920"
          height="1080"
          fetchPriority="high" />

        <div className="absolute inset-0 bg-background/75" />
        <div className="container relative z-10 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mx-auto max-w-3xl font-serif text-4xl font-bold leading-tight text-foreground md:text-6xl">

            Healing Begins With <span className="text-primary">Open Hands</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground md:text-xl">

            We're on a mission to increase access to affordable mental health care for individuals and families who need it most — serving Northeast Ohio from Twinsburg.
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">

            <Button asChild size="lg" className="text-base">
              <Link to="/donate">
                Donate Now <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="text-base">
              <Link to="/about">Learn more about our mission</Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Mission snapshot */}
      <section className="py-16 md:py-24">
        <div className="container grid items-center gap-12 md:grid-cols-2">
          <div>
            <h2 className="font-serif text-3xl font-bold text-foreground md:text-4xl">Our Mission</h2>
            <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
              The mission of Open Hands Healing Foundation is to increase access to affordable mental health care and education by providing free and reduced-fee counseling services to individuals and families who are uninsured, underinsured, or experiencing financial hardship — promoting emotional wellness and mental health equity within underserved communities.
            </p>
            <p className="mt-4 text-sm text-muted-foreground">Services are provided in a space donated by Hand in Hand Counseling Services LLC in Twinsburg, Ohio.

            </p>
          </div>
          <div className="overflow-hidden rounded-2xl">
            <img
              src={missionImage}
              alt="Supportive counseling session in a warm office setting"
              className="h-full w-full object-cover"
              loading="lazy" />

          </div>
        </div>
      </section>

      {/* Focus Areas */}
      <section className="bg-card py-16 md:py-24">
        <div className="container">
          <h2 className="text-center font-serif text-3xl font-bold text-foreground md:text-4xl">
            What We Do
          </h2>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {focusAreas.map((area, i) =>
            <motion.div
              key={area.title}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fade}>

                <Card className="h-full border-border/50 bg-background transition-shadow hover:shadow-md">
                  <CardContent className="flex flex-col items-center p-6 text-center">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                      <area.icon className="h-7 w-7 text-primary" />
                    </div>
                    <h3 className="mt-4 font-serif text-lg font-semibold text-foreground">
                      {area.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                      {area.desc}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="bg-primary py-16 text-primary-foreground">
        <div className="container text-center">
          <h2 className="font-serif text-3xl font-bold md:text-4xl">
            Together, We Can Make a Difference
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-lg opacity-90">
            Your support helps us provide free counseling sessions to those who need it most. Every contribution matters.
          </p>
          <Button
            asChild
            size="lg"
            variant="secondary"
            className="mt-8 text-base">

            <Link to="/donate">Support Our Mission</Link>
          </Button>
        </div>
      </section>

      {/* 501(c)(3) Disclaimer */}
      <section className="bg-accent/10 py-6">
        <div className="container flex items-start gap-3 text-sm text-muted-foreground md:items-center md:justify-center">
          <Info className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
          <p>
            Open Hands Healing Foundation is a registered 501(c)(3) tax-exempt nonprofit. Your donations are tax-deductible to the fullest extent allowed by law.
          </p>
        </div>
      </section>
    </main>);

};

export default Index;